// App bootstrap: build the shell header, wire the auth control, register
// views, and re-render the active view whenever auth state changes (so the
// guest CTA flips to real notes/pin controls right after sign-in).
import "./styles/tailwind.css";
import { subscribe, isAuthed } from "./lib/auth.js";
import { fetchAiProfile } from "./lib/ai.js";
import { renderAuthControl } from "./ui/authPanel.js";
import { initRouter, registerView, navigate, reRenderCurrent, startRoute } from "./router/router.js";
import { searchView, verbDetailView } from "./views/search.js";
import { dashboardView } from "./views/dashboard.js";
import { articleView } from "./views/article.js";
import { groupsView } from "./views/groups.js";
import { guideView } from "./views/guide.js";
import { tensesView } from "./views/tenses.js";
import { nounsView } from "./views/nouns.js";
import { adjectivesView } from "./views/adjectives.js";
import { pronounsView } from "./views/pronouns.js";
import { adverbsView } from "./views/adverbs.js";
import { articlesView } from "./views/articles.js";
import { prepositionsView } from "./views/prepositions.js";
import { conjunctionsView } from "./views/conjunctions.js";
import { interjectionsView } from "./views/interjections.js";
import { objectsView } from "./views/objects.js";
import { locutionsView } from "./views/locutions.js";
import { soundMapView } from "./views/soundMap.js";
import { core100View } from "./views/core100.js";
import { wordOrderView } from "./views/wordOrder.js";
import { regularVerbsView } from "./views/regularVerbs.js";
import { bigFourVerbsView } from "./views/bigFourVerbs.js";
import { irregularVerbsView } from "./views/irregularVerbs.js";
import { stressAccentsView } from "./views/stressAccents.js";
import { wordClassesView } from "./views/wordClasses.js";
import { advancedWordOrderView } from "./views/advancedWordOrder.js";
import { quickGuidesView } from "./views/quickGuides.js";
import { structureView } from "./views/structure.js";
import { trainerView } from "./views/trainer.js";
import { flashcardsView } from "./views/flashcards.js";
import { correctionView } from "./views/correction.js";
import { clozeView } from "./views/cloze.js";
import { reviewView } from "./views/review.js";
import { referenceView } from "./views/reference.js";
import { vocabularyView } from "./views/vocabulary.js";
import { expressionsView } from "./views/expressions.js";
import { dictionaryView } from "./views/dictionary.js";
import { aiTrainerView } from "./views/aiTrainer.js";
import { refineView } from "./views/refine.js";
import { starterView } from "./views/starter.js";
import { adminView } from "./admin/hub.js";
import { fetchNavItems } from "./cms/api.js";
import { workspaceView } from "./views/workspace.js";
import { resetPasswordView } from "./views/resetPassword.js";
import { registerServiceWorker } from "./lib/pwa.js";
import { maybeStartOnboarding } from "./ui/onboarding.js";
import { initAudioDelegation } from "./lib/audio.js";
import { initCommandPalette } from "./ui/commandPalette.js";

// Beginner-first navigation model — the single source of truth for both the
// desktop dropdowns and the mobile bottom-sheet. Three buckets only
// (progressive disclosure); the long tail stays reachable via ⌘K and the
// dashboard pillar grid. Items may be plain nav, carry a `slug` (article) or
// an `inf` (verb-detail), or be a `group` holding a small set of sub-items.
// The optional `tour` field tags a bucket for the first-run onboarding
// spotlight. The bottom-bar tab index maps to this array (0/1/2).
const NAV_MODEL = [
  {
    label: "Explore",
    icon: "🧭",
    tour: "map",
    items: [
      { nav: "guide", label: "The Essentials" },
      { nav: "vocabulary", label: "Vocabulary" },
      {
        group: "Word classes",
        items: [
          { nav: "nouns", label: "Nouns" },
          { nav: "search", label: "Verbs" },
          { nav: "adjectives", label: "Adjectives" },
          { nav: "pronouns", label: "Pronouns" },
          { nav: "adverbs", label: "Adverbs" },
          { nav: "articles", label: "Articles" },
          { nav: "prepositions", label: "Prepositions" },
          { nav: "conjunctions", label: "Conjunctions" },
          { nav: "interjections", label: "Interjections" },
        ],
      },
      {
        group: "Other",
        items: [
          { nav: "objects", label: "Direct & Indirect Objects" },
          { nav: "structure", label: "Sentence Structure" },
          { nav: "locutions", label: "Locutions" },
          { nav: "expressions", label: "Expressions" },
        ],
      },
    ],
  },
  {
    label: "Practice",
    icon: "✍️",
    tour: "practice",
    items: [
      { nav: "refine", label: "Training Overview" },
      { nav: "flashcards", label: "Flashcards" },
      { nav: "cloze", label: "Fill the Gap" },
      { nav: "ai-trainer", label: "AI Translate" },
      { nav: "review", label: "Due for Review" },
      { nav: "workspace", label: "My Workspace" },
    ],
  },
  {
    label: "The Vault",
    icon: "🗝️",
    tour: "verbs",
    items: [
      { nav: "search", label: "Verb Index" },
      { nav: "guide", label: "Grammar Map" },
      { nav: "dictionary", label: "Dictionary" },
      { nav: "quickguides", label: "Quick Guides" },
    ],
  },
];

// Turn CMS-published navigable articles into a "Library" nav bucket. Items with
// a nav_group are sub-grouped; the rest sit flat. Shapes match NAV_MODEL leaves
// ({ nav:"article", slug, label }), so the existing render + click wiring just
// works.
function buildLibraryBucket(items) {
  const groups = {};
  const flat = [];
  for (const it of items) {
    const leaf = { nav: "article", slug: it.slug, label: it.title || it.slug };
    if (it.nav_group) (groups[it.nav_group] = groups[it.nav_group] || []).push(leaf);
    else flat.push(leaf);
  }
  const out = flat.slice();
  for (const g of Object.keys(groups)) out.push({ group: g, items: groups[g] });
  return { label: "Library", icon: "📚", items: out };
}

// Build the data-* attributes a nav button needs so the click wiring can
// reconstruct the route + params (slug for articles, inf for verb-detail).
function navAttrs(it) {
  return [
    `data-nav="${it.nav}"`,
    it.slug ? `data-slug="${it.slug}"` : "",
    it.inf ? `data-inf="${it.inf}"` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function paramsFromDataset(ds) {
  const p = {};
  if (ds.slug) p.slug = ds.slug;
  if (ds.inf) p.inf = ds.inf;
  return p;
}

// ---- Desktop dropdown rendering --------------------------------------
function dropdownLeafHTML(it, indented) {
  return `<button ${navAttrs(it)} class="block w-full text-left text-sm text-sage-700 ${
    indented ? "pl-7" : "px-4"
  } pr-4 py-2 hover:bg-sage-50">${it.label}</button>`;
}

function dropdownItemsHTML(bucket) {
  return bucket.items
    .map((it) =>
      it.group
        ? `<p class="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-sage-400">${it.group}</p>${it.items
            .map((sub) => dropdownLeafHTML(sub, true))
            .join("")}`
        : dropdownLeafHTML(it, false)
    )
    .join("");
}

// ---- Mobile bottom-sheet rendering -----------------------------------
function sheetLeafHTML(it) {
  return `<button ${navAttrs(it)} class="nav-sheet-row">${it.label}<span class="nav-sheet-arrow" aria-hidden="true">→</span></button>`;
}

function sheetItemsHTML(bucket) {
  return bucket.items
    .map((it) =>
      it.group
        ? `<p class="nav-sheet-group">${it.group}</p>${it.items.map(sheetLeafHTML).join("")}`
        : sheetLeafHTML(it)
    )
    .join("");
}

function buildShell() {
  const root = document.getElementById("app-root");
  root.innerHTML = `
    <header class="border-b border-sage-100 bg-white/80 backdrop-blur sticky top-0 z-30">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <button data-nav="dashboard" class="flex items-center gap-2">
          <span class="text-xl">🌊</span>
          <span class="font-bold text-sage-800">Azzurro</span>
          <span class="text-xs text-sage-400 hidden sm:inline">· Italian reference</span>
        </button>
        <div class="flex items-center gap-2">
          <nav class="hidden md:flex items-center gap-1 sm:gap-2">
            ${NAV_MODEL.map(
              (m) => `
              <div class="relative nav-menu"${m.tour ? ` data-tour="${m.tour}"` : ""}>
                <button class="nav-toggle text-sm font-medium text-sage-700 px-3 py-1.5 rounded-lg hover:bg-sage-50 flex items-center gap-1">
                  ${m.label}<span class="text-sage-400 text-xs">▾</span>
                </button>
                <div class="nav-dropdown hidden absolute right-0 mt-1 w-52 bg-white border border-sage-100 rounded-xl shadow-lg py-1 z-40">
                  ${dropdownItemsHTML(m)}
                </div>
              </div>
            `
            ).join("")}
            <button data-cmdk-open data-tour="search" type="button" aria-label="Search (Ctrl or Cmd + K)" class="flex items-center gap-2 text-sm text-sage-500 pl-3 pr-2 py-1.5 rounded-lg border border-sage-200 bg-white hover:bg-sage-50 hover:text-sage-700">
              <span aria-hidden="true">🔍</span>
              <span class="hidden md:inline">Search</span>
              <kbd class="hidden md:inline text-[0.65rem] text-sage-400 bg-sage-50 border border-sage-100 rounded px-1 py-0.5">⌘K</kbd>
            </button>
          </nav>
          <span id="auth-slot" data-tour="auth"></span>
        </div>
      </div>
    </header>
    <main id="app" class="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6"></main>

    <nav class="bottom-nav md:hidden" aria-label="Primary">
      <button data-cmdk-open type="button" class="bottom-nav-btn" aria-label="Search">
        <span class="bottom-nav-icon" aria-hidden="true">🔍</span>
        <span class="bottom-nav-label">Search</span>
      </button>
      ${NAV_MODEL.map(
        (m, i) => `
        <button data-sheet-tab="${i}" type="button" class="bottom-nav-btn" aria-expanded="false">
          <span class="bottom-nav-icon" aria-hidden="true">${m.icon}</span>
          <span class="bottom-nav-label">${m.label}</span>
        </button>
      `
      ).join("")}
    </nav>

    <div id="nav-sheet" class="nav-sheet-root hidden md:hidden" role="dialog" aria-modal="true">
      <div class="nav-sheet-backdrop" data-sheet-close></div>
      <div class="nav-sheet-panel">
        <div class="nav-sheet-handle" aria-hidden="true"></div>
        <h2 class="nav-sheet-title"></h2>
        <div class="nav-sheet-items"></div>
      </div>
    </div>`;

  root.querySelector("#auth-slot").appendChild(renderAuthControl());

  // Dropdown toggle behaviour: clicking a category opens its menu and closes
  // the others; clicking outside closes all.
  const menus = Array.from(root.querySelectorAll(".nav-menu"));
  menus.forEach((menu) => {
    const toggle = menu.querySelector(".nav-toggle");
    const dropdown = menu.querySelector(".nav-dropdown");
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      const isOpen = !dropdown.classList.contains("hidden");
      menus.forEach((m) => m.querySelector(".nav-dropdown").classList.add("hidden"));
      if (!isOpen) dropdown.classList.remove("hidden");
    });
  });
  document.addEventListener("click", () =>
    menus.forEach((m) => m.querySelector(".nav-dropdown").classList.add("hidden"))
  );

  // Static nav buttons present at build time (logo + desktop dropdown leaves).
  // Mobile-sheet rows are rendered on demand and wired by delegation below.
  root.querySelectorAll("[data-nav]").forEach((b) =>
    b.addEventListener("click", () => navigate(b.dataset.nav, paramsFromDataset(b.dataset)))
  );

  // ---- Mobile bottom-sheet open/close --------------------------------
  const sheet = root.querySelector("#nav-sheet");
  const sheetTitle = sheet.querySelector(".nav-sheet-title");
  const sheetItems = sheet.querySelector(".nav-sheet-items");
  const tabButtons = Array.from(root.querySelectorAll("[data-sheet-tab]"));
  let openSheetIdx = -1;

  function updateTabState() {
    tabButtons.forEach((b) => {
      const on = Number(b.dataset.sheetTab) === openSheetIdx;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-expanded", on ? "true" : "false");
    });
  }

  function onSheetKey(e) {
    if (e.key === "Escape") closeSheet();
  }

  function openSheet(i) {
    const bucket = NAV_MODEL[i];
    if (!bucket) return;
    sheetTitle.textContent = bucket.label;
    sheetItems.innerHTML = sheetItemsHTML(bucket);
    sheet.classList.remove("hidden");
    // Next frame so the open-transition has a starting state to animate from.
    requestAnimationFrame(() => sheet.classList.add("is-open"));
    openSheetIdx = i;
    updateTabState();
    document.addEventListener("keydown", onSheetKey);
  }

  function closeSheet() {
    if (openSheetIdx === -1) return;
    sheet.classList.remove("is-open");
    openSheetIdx = -1;
    updateTabState();
    document.removeEventListener("keydown", onSheetKey);
    // Hide after the slide-down settles (matches the CSS transition).
    window.setTimeout(() => {
      if (openSheetIdx === -1) sheet.classList.add("hidden");
    }, 220);
  }

  tabButtons.forEach((btn) =>
    btn.addEventListener("click", () => {
      const i = Number(btn.dataset.sheetTab);
      if (openSheetIdx === i) closeSheet();
      else openSheet(i);
    })
  );
  sheet.querySelector("[data-sheet-close]").addEventListener("click", closeSheet);
  sheetItems.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-nav]");
    if (!btn) return;
    navigate(btn.dataset.nav, paramsFromDataset(btn.dataset));
    closeSheet();
  });

  return root.querySelector("#app");
}

async function start() {
  // Pull CMS-published navigable articles into the menu (a "Library" bucket)
  // before the shell renders. Non-fatal: if the fields/collection aren't there
  // yet, this no-ops and the menu is unchanged.
  try {
    const navItems = await fetchNavItems();
    if (navItems && navItems.length) NAV_MODEL.push(buildLibraryBucket(navItems));
  } catch (_) {
    /* DB-driven nav is optional */
  }
  const mount = buildShell();
  initRouter(mount);
  // One delegated listener powers every 🔊 pronunciation button app-wide.
  initAudioDelegation(document);
  // Global ⌘K / Ctrl-K / "/" search palette, available on every page.
  initCommandPalette();
  registerView("dashboard", dashboardView);
  registerView("article", articleView);
  registerView("search", searchView);
  // verb-detail is handled inline by the search view via params.inf
  registerView("verb-detail", verbDetailView);
  registerView("groups", groupsView);
  registerView("guide", guideView);
  registerView("tenses", tensesView);
  registerView("nouns", nounsView);
  registerView("adjectives", adjectivesView);
  registerView("pronouns", pronounsView);
  registerView("adverbs", adverbsView);
  registerView("articles", articlesView);
  registerView("prepositions", prepositionsView);
  registerView("conjunctions", conjunctionsView);
  registerView("interjections", interjectionsView);
  registerView("objects", objectsView);
  registerView("locutions", locutionsView);
  registerView("soundmap", soundMapView);
  registerView("core100", core100View);
  registerView("wordorder", wordOrderView);
  registerView("regularverbs", regularVerbsView);
  registerView("bigfour", bigFourVerbsView);
  registerView("irregularverbs", irregularVerbsView);
  registerView("stressaccents", stressAccentsView);
  registerView("wordclasses", wordClassesView);
  registerView("advancedwordorder", advancedWordOrderView);
  registerView("quickguides", quickGuidesView);
  registerView("structure", structureView);
  registerView("trainer", trainerView);
  registerView("flashcards", flashcardsView);
  registerView("correction", correctionView);
  registerView("cloze", clozeView);
  registerView("review", reviewView);
  registerView("reference", referenceView);
  registerView("vocabulary", vocabularyView);
  registerView("expressions", expressionsView);
  registerView("dictionary", dictionaryView);
  registerView("ai-trainer", aiTrainerView);
  registerView("refine", refineView);
  registerView("starter", starterView);
  registerView("admin", adminView);
  registerView("workspace", workspaceView);
  registerView("reset-password", resetPasswordView);

  // Re-render the current view on any auth change so personal controls and
  // pin/note overlays refresh without a manual reload.
  let first = true;
  subscribe(() => {
    if (first) {
      first = false;
      return;
    }
    reRenderCurrent();
  });

  // Landing page: a deep-link hash always wins. Otherwise a signed-in learner
  // who has practised a few times lands on Refine (their "where to improve"
  // overview); everyone else lands on the dashboard. The attempt count lives on
  // the profile, so this is a single quick lookup; deep-link and guest paths
  // route synchronously with no wait.
  const hasHash = window.location.hash.replace(/^#\/?/, "").length > 0;
  if (hasHash || !isAuthed()) {
    startRoute("dashboard");
  } else {
    fetchAiProfile()
      .then((p) => {
        const attempts = (p && Number(p.ai_attempts_count)) || 0;
        startRoute(attempts >= 5 ? "refine" : "dashboard");
      })
      .catch(() => startRoute("dashboard"));
  }

  // Production-only PWA registration; no-op on the dev server.
  registerServiceWorker();
  // First-run coachmark tour (shows once per browser, fully skippable).
  maybeStartOnboarding();
}

start();
